import Highcharts from 'highcharts';
import { removeVerticalLabel, renderVerticalLabel } from './renderVerticalLabel';
import { removeAnnotation, renderAnnotation } from './renderAnnotation';
import { formatCurrency } from '../../../../../../../CommonUtils/formatNumber';
import { Theme } from '../../../../../../../theme/theme';
import {
  AnnotationsIds,
  PlanSeriesName,
  RetirementAssetsChartDataDTO,
} from '../RetirementChart.meta';

export const chartRenderEvent = (
  chart: Highcharts.Chart,
  data: RetirementAssetsChartDataDTO,
  theme: Theme,
) => {
  const colorSuccess = theme.colors.success['600'];
  const colorError = theme.colors.error['600'];

  const proposedPlanSeries = chart.series.find(({ name }) => name === PlanSeriesName.Recommended);

  if (!proposedPlanSeries || proposedPlanSeries.points.length === 0) {
    return;
  }

  const {
    PrincipalName,
    PrincipalRetirementIndex,
    PrincipalRetirementAge,
    PrincipalLifeExpectancyIndex,
    PrincipalLifeExpectancy,
    HasSpouse,
    SpouseName,
    SpouseRetirementIndex,
    SpouseRetirementAge,
    SpouseLifeExpectancyIndex,
    SpouseLifeExpectancy,
  } = data;

  const principalRetirementPoint = proposedPlanSeries.points[PrincipalRetirementIndex];
  const spouseRetirementPoint = proposedPlanSeries.points[SpouseRetirementIndex];

  if (principalRetirementPoint) {
    renderVerticalLabel({
      id: AnnotationsIds.PrincipalRetirement,
      chart,
      point: principalRetirementPoint,
      value: PrincipalRetirementAge,
      label: HasSpouse ? `${PrincipalName}'s <br/> Retirement Age` : 'Retirement Age',
      offset: 0,
      color: theme.colors.primary['600'],
      theme,
    });
  } else {
    removeVerticalLabel(AnnotationsIds.PrincipalRetirement);
  }

  const principalLifeExpectancyPoint = proposedPlanSeries.points[PrincipalLifeExpectancyIndex];

  if (principalLifeExpectancyPoint) {
    renderVerticalLabel({
      id: AnnotationsIds.PrincipalLife,
      chart,
      point: principalLifeExpectancyPoint,
      value: PrincipalLifeExpectancy,
      label: HasSpouse ? `${PrincipalName}'s <br/> Life Expectancy` : 'Life Expectancy',
      offset: 0,
      color:
        principalLifeExpectancyPoint.y && principalLifeExpectancyPoint.y > 0
          ? colorSuccess
          : colorError,
      theme,
    });
  } else {
    removeVerticalLabel(AnnotationsIds.PrincipalLife);
  }

  if (HasSpouse) {
    if (spouseRetirementPoint) {
      renderVerticalLabel({
        id: AnnotationsIds.SpouseRetirement,
        chart,
        point: spouseRetirementPoint,
        value: SpouseRetirementAge,
        label: `${SpouseName}'s <br/> Retirement Age`,
        offset: 45,
        color: theme.colors.primary['600'],
        theme,
      });
    } else {
      removeVerticalLabel(AnnotationsIds.SpouseRetirement);
    }

    const spouseLifeExpectancyPoint = proposedPlanSeries.points[SpouseLifeExpectancyIndex];

    if (spouseLifeExpectancyPoint) {
      renderVerticalLabel({
        id: AnnotationsIds.SpouseLife,
        chart,
        point: spouseLifeExpectancyPoint,
        value: SpouseLifeExpectancy,
        label: `${SpouseName}'s <br/> Life Expectancy`,
        offset: 45,
        color:
          spouseLifeExpectancyPoint.y && spouseLifeExpectancyPoint.y > 0
            ? colorSuccess
            : colorError,
        theme,
      });
    } else {
      removeVerticalLabel(AnnotationsIds.SpouseLife);
    }
  } else {
    removeVerticalLabel(AnnotationsIds.SpouseRetirement);
    removeVerticalLabel(AnnotationsIds.SpouseLife);
  }

  const isSpouseRetiringFirst = HasSpouse && PrincipalRetirementIndex > SpouseRetirementIndex;

  const retirementPoint = isSpouseRetiringFirst ? spouseRetirementPoint : principalRetirementPoint;

  if (retirementPoint) {
    renderAnnotation({
      id: AnnotationsIds.FundsAtRetirement,
      chart,
      point: retirementPoint,
      className: 'funds-annotation',
      // ADV-5867 - Hide the label when the amount is 0
      label: retirementPoint?.y
        ? `<span style='text-align: center; font-size: 12px; font-weight: 500; fill: ${
            theme.colors.primary['500']
          };'>Funds at Retirement Start</span><br><span style='font-size: 12px; font-weight: 600; text-align: center; fill: ${
            theme.colors.primary['500']
          };'>${formatCurrency(retirementPoint.y)}</span>`
        : '',
    });
  } else {
    removeAnnotation(AnnotationsIds.FundsAtRetirement);
  }
};
